/**
 * D619 (owner 2026-10-18: "indicadores, talvez gráficos em cima, referente só àquela pessoa"): the charts under the KPI row of a person
 * record. Every number comes from the ANALYTICS database (the BI read model the ETL fills from the operational one) — the rider's
 * `GET /v1/analytics/customers/:id/summary` and the driver's `GET /v1/analytics/drivers/:id/insights` — never from an operational read.
 * A chart the console cannot read says why in its empty line (no market in scope, refused, failed), like the KPI tiles.
 */
import { type ReactElement } from 'react';
import { type CustomerSummary, type DriverInsights } from '@vello/api-client';
import { ColumnChart, ComboChart, Grid, Panel, SectionTitle } from '@vello/web-theme';
import { useT } from '../i18n/provider';
import { useVocab } from '../i18n/vocab';
import { type CardState } from './person-card';
import { usePersonFormat } from './person-format';

/** Why a chart is empty, in words. */
function useEmptyLine(): (state: CardState<unknown> | undefined, none: string) => string {
  const { t } = useT();
  return (state, none) => {
    if (state === undefined) return t('prec.kpi.needsMarket');
    if (state.status === 'error') return t(state.error.status === 403 || state.error.code === 'FORBIDDEN' ? 'prec.kpi.refused' : 'prec.kpi.failed');
    return none;
  };
}

/** The minor-unit digits of a currency (2 for most, 0 for a few) — a chart axis draws major units. */
function digitsOf(currency: string): number {
  try {
    return new Intl.NumberFormat('en', { style: 'currency', currency }).resolvedOptions().maximumFractionDigits ?? 2;
  } catch {
    return 2;
  }
}

/** A rider: what was asked and what was done per service, and what was spent per service (one currency only). */
export function RiderCharts({ summary }: { readonly summary: CardState<CustomerSummary> | undefined }): ReactElement {
  const { t } = useT();
  const { v } = useVocab();
  const fmt = usePersonFormat();
  const empty = useEmptyLine();
  const ready = summary?.status === 'ready' ? summary.value : undefined;
  const verticals = ready?.verticals ?? [];
  const any = verticals.some((x) => x.requested > 0);
  const currencies = new Set(verticals.flatMap((x) => (x.spend !== null ? [x.spend.currency] : [])));
  const currency = currencies.size === 1 ? [...currencies][0] : undefined;
  const scale = currency !== undefined ? 10 ** digitsOf(currency) : 1;
  return (
    <Grid min="md">
      <Panel>
        <SectionTitle level={3} size="sub">{t('prec.chart.byService')}</SectionTitle>
        <ColumnChart
          title={t('prec.chart.byService')}
          categories={any ? verticals.map((x) => v('common.vertical', x.vertical)) : []}
          series={any ? [
            { key: 'completed', label: t('prec.activity.completed'), values: verticals.map((x) => x.completed) },
            { key: 'cancelled', label: t('prec.activity.cancelled'), values: verticals.map((x) => x.cancelled) },
          ] : []}
          formatValue={(n) => fmt.count(n)}
          loading={summary?.status === 'loading'}
          loadingLabel={t('ui.loading')}
          emptyLabel={empty(summary, t('prec.activity.empty'))}
          height={200}
        />
      </Panel>
      <Panel>
        <SectionTitle level={3} size="sub">{t('prec.chart.spendByService')}</SectionTitle>
        <ColumnChart
          title={t('prec.chart.spendByService')}
          categories={any && currency !== undefined ? verticals.map((x) => v('common.vertical', x.vertical)) : []}
          series={any && currency !== undefined ? [{ key: 'spend', label: t('prec.activity.spend'), values: verticals.map((x) => (x.spend !== null ? Number(x.spend.amountMinor) / scale : 0)) }] : []}
          formatValue={(n) => (currency !== undefined ? fmt.money({ amountMinor: String(Math.round(n * scale)), currency }) : String(n))}
          loading={summary?.status === 'loading'}
          loadingLabel={t('ui.loading')}
          emptyLabel={empty(summary, t('prec.kpi.spend.none'))}
          height={200}
        />
      </Panel>
    </Grid>
  );
}

/** A provider: rides and net earnings week by week, and — when he also delivers — deliveries day by day. */
export function DriverCharts({ insights }: { readonly insights: CardState<DriverInsights> | undefined }): ReactElement {
  const { t } = useT();
  const fmt = usePersonFormat();
  const empty = useEmptyLine();
  const ready = insights?.status === 'ready' ? insights.value : undefined;
  const block = ready?.earnings[0];
  const weeks = block?.series.week ?? [];
  const active = weeks.some((w) => w.rides > 0 || w.netMinor !== '0');
  const scale = block !== undefined ? 10 ** digitsOf(block.currency) : 1;
  const days = ready?.courier?.deliveries.days ?? [];
  const delivering = days.some((d) => d.completed > 0 || d.cancelled > 0);
  const loading = insights?.status === 'loading';
  return (
    <Grid min="md">
      <Panel>
        <SectionTitle level={3} size="sub">{t('prec.dchart.weeks')}</SectionTitle>
        <ComboChart
          title={t('prec.dchart.weeks')}
          categories={active ? weeks.map((w) => fmt.day(`${w.key}T12:00:00Z`)) : []}
          bars={active ? [{ key: 'rides', label: t('prec.dchart.rides'), values: weeks.map((w) => w.rides) }] : []}
          lines={active && block !== undefined ? [{ key: 'net', label: t('prec.dchart.net'), values: weeks.map((w) => Number(w.netMinor) / scale) }] : []}
          formatValue={(n) => fmt.count(n)}
          formatRight={(n) => (block !== undefined ? fmt.money({ amountMinor: String(Math.round(n * scale)), currency: block.currency }) : String(n))}
          loading={loading}
          loadingLabel={t('ui.loading')}
          emptyLabel={empty(insights, t('prec.dchart.empty'))}
          height={220}
        />
      </Panel>
      <Panel>
        <SectionTitle level={3} size="sub">{t('prec.dchart.deliveries')}</SectionTitle>
        <ColumnChart
          title={t('prec.dchart.deliveries')}
          categories={delivering ? days.map((d) => fmt.day(`${d.date}T12:00:00Z`)) : []}
          series={delivering ? [
            { key: 'completed', label: t('prec.activity.completed'), values: days.map((d) => d.completed) },
            { key: 'cancelled', label: t('prec.activity.cancelled'), values: days.map((d) => d.cancelled) },
          ] : []}
          formatValue={(n) => fmt.count(n)}
          loading={loading}
          loadingLabel={t('ui.loading')}
          emptyLabel={empty(insights, t('prec.dchart.deliveries.empty'))}
          height={220}
        />
      </Panel>
    </Grid>
  );
}
